import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import { Html5Qrcode } from "html5-qrcode";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { validateTicket } from "../api";
import { getCityDisplay } from "../utils/city";
import dayjs from "dayjs";

const CheckTicket = () => {
  const { t } = useTranslation("common");
  const [searchParams] = useSearchParams();
  const [bookingId, setBookingId] = useState("");
  const [passengerId, setPassengerId] = useState("");
  const [ticketInfo, setTicketInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(true);
  const qrRegionId = "qr-reader";
  const html5QrCodeRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:768px)");

  const handleCheckTicket = async (bId, pId) => {
    setError("");
    setTicketInfo(null);
    setLoading(true);
    try {
      const data = await validateTicket(bId, pId);
      setTicketInfo(data);
    } catch (err) {
      const msg = (typeof err === "string" ? err : err?.response?.data?.error) || t("checkTicket.unknownError");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleScanSuccess = (decodedText) => {
    try {
      const url = new URL(decodedText);
      const b = url.searchParams.get("booking");
      const p = url.searchParams.get("passenger");
      if (b && p) {
        setBookingId(b);
        setPassengerId(p);
        handleCheckTicket(b, p);
        stopScanner();
      } else {
        setError(t("checkTicket.emptyQr"));
      }
    } catch {
      setError(t("checkTicket.badQr"));
    }
  };

  const startScanner = () => {
    setScanning(true);
    const config = {
      fps: 10,
      qrbox: {
        width: isMobile ? 200 : 260,
        height: isMobile ? 200 : 260,
      },
      aspectRatio: 1.0,
    };

    const html5QrCode = new Html5Qrcode(qrRegionId);
    html5QrCodeRef.current = html5QrCode;

    html5QrCode
      .start(
        { facingMode: "environment" },
        config,
        (decodedText) => handleScanSuccess(decodedText),
        () => {}
      )
      .then(() => {
        if (html5QrCodeRef.current !== html5QrCode) {
          releaseScanner(html5QrCode);
        }
      })
      .catch((err) => {
        if (html5QrCodeRef.current !== html5QrCode) return;
        const message = err?.message || String(err);
        setError(t("checkTicket.scannerStartError", { message }));
        setScanning(false);
      });
  };

  const releaseScanner = (scanner) => {
    if (!scanner) return;
    // stop() throws synchronously until the camera is running or paused.
    // That throw in an effect cleanup is uncaught and blanks the page.
    try {
      Promise.resolve(scanner.stop())
        .then(() => scanner.clear())
        .catch(() => {});
    } catch {
      // start() has not settled; its then() stops the camera afterwards.
    }
  };

  const stopScanner = () => {
    const scanner = html5QrCodeRef.current;
    html5QrCodeRef.current = null;
    releaseScanner(scanner);
    setScanning(false);
  };

  useEffect(() => {
    startScanner();
    return () => {
      const scanner = html5QrCodeRef.current;
      html5QrCodeRef.current = null;
      releaseScanner(scanner);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const bId = searchParams.get("booking");
    const pId = searchParams.get("passenger");
    if (bId && pId) {
      setBookingId(bId);
      setPassengerId(pId);
      handleCheckTicket(bId, pId);
    }
  }, [searchParams]);

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 2, px: 2, textAlign: "center" }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t("checkTicket.title")}
      </Typography>

      <Box
        id={qrRegionId}
        sx={{
          width: isMobile ? 260 : 320,
          height: isMobile ? 260 : 320,
          margin: "0 auto",
          borderRadius: 2,
          overflow: "hidden",
          border: "2px solid #E0E0E0",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          mb: 3,
          backgroundColor: "#000",
        }}
      />

      {!scanning && (
        <Button
          variant="outlined"
          onClick={startScanner}
          sx={{ mb: 3 }}
          startIcon={<QrCodeScannerIcon />}
        >
          {t("checkTicket.scanAgain")}
        </Button>
      )}

      <TextField
        label={t("admin.bookingNumber")}
        fullWidth
        variant="outlined"
        value={bookingId}
        onChange={(e) => setBookingId(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label={t("admin.passengerId")}
        fullWidth
        variant="outlined"
        value={passengerId}
        onChange={(e) => setPassengerId(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={() => handleCheckTicket(bookingId, passengerId)}
        disabled={loading}
        sx={{ mb: 3 }}
      >
        {t("checkTicket.checkManually")}
      </Button>

      {loading && <CircularProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {ticketInfo && (
        <Card
          sx={{
            mt: 3,
            mb: 3,
            border: "2px solid",
            borderColor: ticketInfo.status === "success" ? "green" : "red",
          }}
        >
          <CardContent>
            <Typography
              variant="h5"
              fontWeight="bold"
              color={ticketInfo.status === "success" ? "green" : "red"}
              mb={2}
            >
              {ticketInfo.status === "success" ? <CheckCircleIcon /> : <BlockIcon />}{" "}
              {ticketInfo.message}
            </Typography>
            <Typography>
              <strong>{t("checkTicket.passenger")}</strong> {ticketInfo.passenger.name} {ticketInfo.passenger.surname}
            </Typography>
            <Typography>
              <strong>{t("checkTicket.route")}</strong> {getCityDisplay(ticketInfo.route.from)} → {getCityDisplay(ticketInfo.route.to)}
            </Typography>
            {
              ticketInfo.payment_status === "paid" && (
                <Typography>
                  <strong>{t("checkTicket.paymentStatus")}</strong> {t("checkTicket.paid")}
                </Typography>
              )
            }
            {
              ticketInfo.payment_status === "not_paid" && (
                <Typography>
                  <strong>{t("checkTicket.paymentStatus")}</strong> {t("checkTicket.unpaid")}
                </Typography>
              )
            }
            {
              ticketInfo?.price && (
                <Typography>
                  <strong>{t("checkTicket.price")}</strong> {t("units.uahAmount", { amount: ticketInfo.price })}
                </Typography>
              )
            }
            <Typography>
              <strong>{t("checkTicket.date")}</strong> {dayjs(ticketInfo.route.departure_date).format("DD.MM.YYYY")}
            </Typography>
            <Typography>
              <strong>{t("checkTicket.time")}</strong> {dayjs(ticketInfo.route.departure_time).format("HH:mm")}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CheckTicket;
